import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { ArrowRight, Calendar, Loader2 } from "lucide-react";
import { getBlogPosts } from "../api";

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1706606992658-b7225196f1af";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogPosts().then((d) => setPosts(d.posts || [])).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div>
      <Seo title="Weekly Property Blog — Bath Market News & Guides" description="Property insight from Anadear Estates: Bath & Bristol market updates, buyer and landlord guides, and local area news." path="/blog" />
      <section className="relative h-[320px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img src={FALLBACK_IMG} alt="Weekly Blog" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center px-5">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#e8d4b8] mb-3">News &amp; Insights</p>
          <h1 className="font-display text-white text-[40px] md:text-[56px] font-300">Weekly Blog</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          {loading ? (
            <div className="py-20 text-center text-[#8a8a8a] flex items-center justify-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading posts…</div>
          ) : posts.length === 0 ? (
            <div className="py-20 text-center" data-testid="blog-empty">
              <p className="font-display text-[24px] text-[#2b2b2b] mb-2">No articles yet</p>
              <p className="text-[14px] text-[#8a8a8a]">Our latest market news and guides will appear here soon.</p>
            </div>
          ) : (
            <>
              <Link to={`/blog/${featured.slug}`} className="grid lg:grid-cols-2 gap-10 items-center mb-20 group cursor-pointer" data-testid="blog-featured">
                <div className="overflow-hidden rounded-2xl">
                  <img src={featured.coverImage || FALLBACK_IMG} alt={featured.title} className="w-full h-[400px] object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 rounded-full bg-[#faf7f2] text-[12px] tracking-[0.1em] uppercase text-[#b8935f] border border-[#f0ece5]">{featured.category}</span>
                    <span className="flex items-center gap-1.5 text-[13px] text-[#8a8a8a]"><Calendar size={14} /> {formatDate(featured.publishedAt)}</span>
                  </div>
                  <h2 className="font-display text-[30px] md:text-[36px] font-300 text-[#2b2b2b] leading-tight mb-4 group-hover:text-[#b8935f] transition-colors">{featured.title}</h2>
                  <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-6">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-2 text-[14px] font-500 text-[#b8935f] group-hover:gap-3 transition-all">Read article <ArrowRight size={16} /></span>
                </div>
              </Link>

              {rest.length > 0 && (
                <div className="grid md:grid-cols-3 gap-8">
                  {rest.map((post) => (
                    <Link to={`/blog/${post.slug}`} key={post.id} className="group cursor-pointer" data-testid={`blog-card-${post.slug}`}>
                      <div className="overflow-hidden rounded-xl mb-5">
                        <img src={post.coverImage || FALLBACK_IMG} alt={post.title} className="w-full h-[240px] object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                      </div>
                      <div className="flex items-center gap-3 mb-3 text-[12px] text-[#8a8a8a]">
                        <span className="text-[#b8935f] tracking-[0.1em] uppercase">{post.category}</span>
                      </div>
                      <h3 className="font-display text-[21px] font-500 text-[#2b2b2b] leading-snug mb-3 group-hover:text-[#b8935f] transition-colors">{post.title}</h3>
                      <p className="text-[14px] text-[#6a6a6a] leading-relaxed mb-3">{post.excerpt}</p>
                      <span className="text-[13px] text-[#8a8a8a]">{formatDate(post.publishedAt)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
